import React, { useState, useEffect, useMemo } from "react";
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie,
} from "recharts";
import { DatePicker, Button, Table } from "antd";
import dayjs from "dayjs";
import axios from "../../utils/axiosDash";

// ── DESIGN TOKENS ─────────────────────────────────────────────────────────
const C = {
  red:         "#E24B4A",
  redLight:    "#FCEBEB",
  amber:       "#BA7517",
  amberLight:  "#FAEEDA",
  amberMid:    "#FAC775",
  green:       "#3B6D11",
  greenLight:  "#EAF3DE",
  greenMid:    "#97C459",
  blue:        "#185FA5",
  blueLight:   "#E6F1FB",
  blueMid:     "#378ADD",
  teal:        "#0F6E56",
  tealLight:   "#E1F5EE",
  purple:      "#534AB7",
  purpleLight: "#EFEDFB",
  gray:        "#888780",
  grayLight:   "#F1EFE8",
  dark:        "#2C2C2A",
  border:      "rgba(0,0,0,0.08)",
};

// Normalise PPE class names (API mixes "helmet" and "Gloves" casing)
const normClass = (s) => (s || "").toString().trim().toLowerCase();

const PPE_META = {
  helmet:      { color: C.blue,   bg: C.blueLight,   label: "Helmet"      },
  safety_shoe: { color: C.teal,   bg: C.tealLight,   label: "Safety Shoe" },
  gloves:      { color: C.amber,  bg: C.amberLight,  label: "Gloves"      },
  vest:        { color: C.purple, bg: "#EFEDFB",     label: "Vest"        },
  mask:        { color: C.green,  bg: C.greenLight,  label: "Mask"        },
  goggles:     { color: C.red,    bg: C.redLight,     label: "Goggles"    },
  earplug:     { color: "#9D5B26", bg: "#F6E8DB",     label: "Earplug"    },
  harness:     { color: "#5B6B8C", bg: "#E7EAF2",      label: "Harness"    },
};

const AUTO_COLORS = [
  { color: "#7C5CD6", bg: "#EFE9FB" },
  { color: "#1E9E8C", bg: "#DEF5F0" },
  { color: "#C9521B", bg: "#FBE6DA" },
  { color: "#3B6FB6", bg: "#E3EDFA" },
  { color: "#B4406F", bg: "#FBE3EC" },
  { color: "#6B8E23", bg: "#EDF3DE" },
  { color: "#A3792B", bg: "#F8EEDA" },
  { color: "#4B7B8C", bg: "#E1EFF2" },
];

// Title-case a raw class string like "safety_shoe" or "ear_muff" -> "Safety Shoe"
const titleCase = (s) =>
  (s || "")
    .toString()
    .replace(/[_-]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map(w => w[0].toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

const ppeMeta = (cls) => {
  const key = normClass(cls);
  if (PPE_META[key]) return PPE_META[key];
  const auto = AUTO_COLORS[hashStr(key) % AUTO_COLORS.length];
  return { color: auto.color, bg: auto.bg, label: titleCase(cls) };
};

const PALETTE = [C.blueMid, C.red, C.amberMid, C.greenMid, C.purple, C.teal];

// Is this row a person-count event rather than a PPE event?
const isPersonCountRow = (item) =>
  item.type === "person_count" ||
  item.alertType === "person_count" ||
  (typeof item.person_count === "number" && !(item.ppe_classes && item.ppe_classes.length));

function processData(rawData) {
  if (!rawData?.length) return {
    aggregatedByCam: [],
    violationTimeSeries: [],
    classTimeSeries: [],
    personCountTimeSeries: [],
    recentEvents: [],
    classDistribution: [],
    camIds: [],
    classKeys: [],
    total: 0,
    totalPersonCount: 0,
    totalPersonEvents: 0,
    avgPersonCount: 0,
    peakPersonCount: 0,
    byClass: {},
  };

  const rows = rawData.map(item => ({
    ...item,
    _time: item.timestamp || new Date().toISOString(),
    _isPersonCount: isPersonCountRow(item),
    _classes: (item.ppe_classes || []).map(c => c.toString()),
    _personCount: typeof item.person_count === "number" ? item.person_count : 0,
    _image:
        item.image_url ||
        (item.imgUrls && item.imgUrls[0]) ||
        null,
    }));

  const ppeRows = rows.filter(r => !r._isPersonCount);
  const personRows = rows.filter(r => r._isPersonCount);

  const sorted = [...ppeRows].sort((a, b) => new Date(a._time) - new Date(b._time));
  const personSorted = [...personRows].sort((a, b) => new Date(a._time) - new Date(b._time));

  const classKeyMap = {};
  ppeRows.forEach(r => r._classes.forEach(c => {
    const key = normClass(c);
    if (!classKeyMap[key]) classKeyMap[key] = ppeMeta(c).label;
  }));
  const classKeys = Object.keys(classKeyMap);

  const camTotals = {};
  const ensureCam = (cam) => {
    if (!camTotals[cam]) {
      camTotals[cam] = {
        cam,
        count: 0,
        classCounts: {},
        personCountTotal: 0,
        personCountEvents: 0,
        peakPersonCount: 0,
      };
    }
    return camTotals[cam];
  };
  ppeRows.forEach(r => {
    const cam = r.camera_id || "unknown";
    const entry = ensureCam(cam);
    entry.count++;
    r._classes.forEach(c => {
      const key = normClass(c);
      entry.classCounts[key] = (entry.classCounts[key] || 0) + 1;
    });
  });
  personRows.forEach(r => {
    const cam = r.camera_id || "unknown";
    const entry = ensureCam(cam);
    entry.personCountTotal += r._personCount;
    entry.personCountEvents += 1;
    entry.peakPersonCount = Math.max(entry.peakPersonCount, r._personCount);
  });
  const aggregatedByCam = Object.values(camTotals).sort((a, b) => b.count - a.count);

  const bucketMap = {};
  sorted.forEach(r => {
    const t = dayjs(r._time).format("HH:00");
    if (!bucketMap[t]) bucketMap[t] = { time: t, count: 0 };
    bucketMap[t].count++;
  });
  const violationTimeSeries = Object.values(bucketMap).slice(-30);

  const classBucketMap = {};
  sorted.forEach(r => {
    const t = dayjs(r._time).format("HH:00");
    if (!classBucketMap[t]) {
      classBucketMap[t] = { time: t };
      classKeys.forEach(k => { classBucketMap[t][k] = 0; });
    }
    r._classes.forEach(c => {
      const key = normClass(c);
      classBucketMap[t][key] = (classBucketMap[t][key] || 0) + 1;
    });
  });
  const classTimeSeries = Object.values(classBucketMap).slice(-30);

  const personBucketMap = {};
  personSorted.forEach(r => {
    const t = dayjs(r._time).format("HH:00");
    if (!personBucketMap[t]) personBucketMap[t] = { time: t, count: 0 };
    personBucketMap[t].count = Math.max(personBucketMap[t].count, r._personCount);
  });
  const personCountTimeSeries = Object.values(personBucketMap).slice(-30);

  const recentEvents = [...rows].sort((a, b) => new Date(b._time) - new Date(a._time));

  const byClass = {};
  ppeRows.forEach(r => r._classes.forEach(c => {
    const key = normClass(c);
    byClass[key] = (byClass[key] || 0) + 1;
  }));
  const classDistribution = Object.entries(byClass).map(([key, value]) => ({
    name: classKeyMap[key] || key,
    key,
    value,
    color: ppeMeta(key).color,
  })).sort((a, b) => b.value - a.value);

  const totalPersonCount = personRows.reduce((sum, r) => sum + r._personCount, 0);
  const totalPersonEvents = personRows.length;
  const avgPersonCount = totalPersonEvents > 0 ? Math.round(totalPersonCount / totalPersonEvents) : 0;
  const peakPersonCount = personRows.reduce((max, r) => Math.max(max, r._personCount), 0);

  return {
    aggregatedByCam,
    violationTimeSeries,
    classTimeSeries,
    personCountTimeSeries,
    recentEvents,
    classDistribution,
    camIds: aggregatedByCam.map(c => c.cam),
    classKeys,
    total: ppeRows.length,
    totalPersonCount,
    totalPersonEvents,
    avgPersonCount,
    peakPersonCount,
    byClass,
  };
}

const ChartTooltip = ({ active, payload, label, unit = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}>
      <p style={{ fontWeight: 700, color: C.dark, marginBottom: 6, fontSize: 11 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill, margin: "2px 0", fontWeight: 600 }}>
          {p.name}: <span style={{ color: C.dark }}>{p.value}{unit}</span>
        </p>
      ))}
    </div>
  );
};

// ── KPI CARD ───────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, color, bg }) => (
  <div style={{
    background: bg || "#fff",
    border: `1px solid ${color}22`,
    borderRadius: 16,
    padding: "20px 24px",
    position: "relative",
    overflow: "hidden",
    flex: 1,
    minWidth: 160,
  }}>
    <div style={{ position: "absolute", top: 0, right: 0, width: 72, height: 72, background: `${color}12`, borderRadius: "0 16px 0 72px" }} />
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: C.gray, marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 38, fontWeight: 800, color, lineHeight: 1, marginBottom: 6 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.gray }}>{sub}</div>}
  </div>
);

// ── PPE CLASS PILL ─────────────────────────────────────────────────────────
const ClassPill = ({ cls }) => {
  const m = ppeMeta(cls);
  return (
    <span style={{ background: m.bg, color: m.color, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
      {m.label}
    </span>
  );
};

// ── PERSON COUNT PILL ──────────────────────────────────────────────────────
const PersonCountPill = ({ count }) => (
  <span style={{ background: C.purpleLight, color: C.purple, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
    👤 {count} {count === 1 ? "Person" : "Persons"}
  </span>
);

// ── FEED ROW ───────────────────────────────────────────────────────────────
const FeedRow = ({ event }) => {
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 16px", borderBottom: `1px solid ${C.border}`, transition: "background 0.15s", cursor: "default" }}
      onMouseEnter={e => e.currentTarget.style.background = C.grayLight}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      {event._image ? (
        <img src={event._image} alt="" style={{ width: 52, height: 38, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
      ) : (
        <div style={{ width: 52, height: 38, borderRadius: 6, background: C.grayLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
          📷
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: C.dark }}>{event.camera_id}</span>
          {event._isPersonCount
            ? <PersonCountPill count={event._personCount} />
            : event._classes.map((c, i) => <ClassPill key={i} cls={c} />)}
        </div>
        <div style={{ fontSize: 11, color: C.gray, marginTop: 3 }}>
          {dayjs(event._time).format("MMM D, HH:mm:ss")}
        </div>
      </div>
      <div style={{ fontSize: 11, color: C.gray, flexShrink: 0, textAlign: "right" }}>
        <div style={{ fontWeight: 600, color: C.dark }}>#{event._id?.slice(-6)}</div>
      </div>
    </div>
  );
};

// ── PANEL ──────────────────────────────────────────────────────────────────
const Panel = ({ children, style = {} }) => (
  <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 16, padding: "20px", ...style }}>
    {children}
  </div>
);

const SectionHead = ({ title, sub }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontWeight: 800, fontSize: 14, color: C.dark }}>{title}</div>
    {sub && <div style={{ fontSize: 11, color: C.gray, marginTop: 2 }}>{sub}</div>}
  </div>
);

// ── MAIN ───────────────────────────────────────────────────────────────────
export default function Reports() {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [feedFilter, setFeedFilter] = useState("All");

  const d = useMemo(() => processData(rawData), [rawData]);

  const fetchData = async (date) => {
    try {
      setLoading(true);
      const res = await axios.post("/fetch-for-alerts-reports-prestige", {
        filterDate: (date || selectedDate).format("YYYY-MM-DD"),
      });
      if (res.data.success) setRawData(res.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(selectedDate); }, [selectedDate]);

  const axisProps = { tick: { fontSize: 10, fill: C.gray }, tickLine: false, axisLine: false };

  // Filtered feed (feedFilter is either "All", "person_count", or a PPE class key)
  const filteredFeed = feedFilter === "All"
    ? d.recentEvents
    : feedFilter === "person_count"
      ? d.recentEvents.filter(e => e._isPersonCount)
      : d.recentEvents.filter(e => !e._isPersonCount && e._classes.some(c => normClass(c) === feedFilter));

  // Event log table — one row per camera, now including person count columns
  const eventColumns = [
    {
      title: "Camera", dataIndex: "cam", key: "cam",
      render: v => <span style={{ fontWeight: 700, color: C.dark, fontSize: 13 }}>{v}</span>,
    },
    {
      title: "PPE Violations", key: "classCounts",
      render: (_, row) => (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {Object.entries(row.classCounts).map(([k, v]) => (
            <span key={k} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <ClassPill cls={k} /> <span style={{ fontSize: 11, color: C.gray }}>×{v}</span>
            </span>
          ))}
          {Object.keys(row.classCounts).length === 0 && (
            <span style={{ fontSize: 11, color: C.gray }}>—</span>
          )}
        </div>
      ),
    },
    {
      title: "Person Count", key: "personCount",
      render: (_, row) =>
        row.personCountEvents > 0 ? (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <PersonCountPill count={row.personCountTotal} />
            <span style={{ fontSize: 11, color: C.gray }}>peak {row.peakPersonCount}</span>
          </span>
        ) : (
          <span style={{ fontSize: 11, color: C.gray }}>—</span>
        ),
      sorter: (a, b) => a.personCountTotal - b.personCountTotal,
    },
    {
      title: "Total PPE Events", dataIndex: "count", key: "count",
      render: v => <span style={{ fontWeight: 800, fontSize: 15, color: C.dark }}>{v}</span>,
      sorter: (a, b) => a.count - b.count,
      defaultSortOrder: "descend",
    },
  ];

  const TABS = [
    { key: "overview",  label: "Overview" },
    { key: "cameras",   label: "Camera Analytics" },
    { key: "feed",      label: "Violations" },
    { key: "events",    label: "Event Log" },
  ];

  return (
    <div style={{ padding: "28px 32px", minHeight: "100vh", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800&display=swap" rel="stylesheet" />
      <div style={{ maxWidth: 1560, margin: "0 auto" }}>

        {/* HEADER */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 32, flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, boxShadow: `0 0 0 3px ${C.greenLight}` }} />
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", color: C.teal }}>Live Analytics</span>
            </div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: C.dark, letterSpacing: "-0.5px" }}>Video Surveillance Reports</h1>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: C.gray }}>Missing-PPE detections and occupancy counts across all surveillance cameras</p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <DatePicker
              value={selectedDate}
              onChange={val => setSelectedDate(val || dayjs())}
              format="MMM D, YYYY"
              allowClear={false}
              style={{ borderRadius: 40, height: 40 }}
            />
            <Button
              onClick={() => fetchData(selectedDate)}
              loading={loading}
              style={{ borderRadius: 40, height: 40, fontWeight: 700, background: C.dark, color: "#fff", border: "none", fontFamily: "inherit" }}
            >
              ↻ Refresh
            </Button>
          </div>
        </div>

        {/* KPI STRIP */}
        <div style={{ display: "flex", gap: 16, marginBottom: 28, flexWrap: "wrap" }}>
          <KpiCard label="Total Events" value={d.total} sub="All PPE violations" color={C.dark} bg="#fff" />
          {d.classDistribution.slice(0, 4).map(cls => (
            <KpiCard
              key={cls.key}
              label={cls.name}
              value={cls.value}
              sub="Detections"
              color={cls.color}
              bg={ppeMeta(cls.key).bg}
            />
          ))}
          <KpiCard label="Cameras" value={d.camIds.length} sub="Active in period" color={C.teal} bg={C.tealLight} />
          {/* <KpiCard label="Peak Person Count" value={d.peakPersonCount} sub={`Avg ${d.avgPersonCount} per event`} color={C.purple} bg={C.purpleLight} /> */}
        </div>

        {/* TABS CONTAINER */}
        <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 20, overflow: "hidden" }}>
          <div style={{ borderBottom: `1px solid ${C.border}`, padding: "0 24px" }}>
            <div style={{ display: "flex" }}>
              {TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  style={{
                    padding: "16px 20px", border: "none",
                    borderBottom: activeTab === t.key ? `2px solid ${C.dark}` : "2px solid transparent",
                    background: "none",
                    fontWeight: activeTab === t.key ? 800 : 500,
                    fontSize: 13,
                    color: activeTab === t.key ? C.dark : C.gray,
                    cursor: "pointer", transition: "all 0.15s", fontFamily: "inherit",
                  }}
                >{t.label}</button>
              ))}
            </div>
          </div>

          <div style={{ padding: "24px" }}>

            {/* ── OVERVIEW ── */}
            {activeTab === "overview" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 24 }}>

                  {/* Violation volume over time */}
                  <Panel>
                    <SectionHead title="Violation Volume" sub="PPE violations detected over time" />
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={d.violationTimeSeries} barCategoryGap="35%">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0eeea" vertical={false} />
                        <XAxis dataKey="time" {...axisProps} />
                        <YAxis {...axisProps} allowDecimals={false} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar dataKey="count" name="Violations" fill={C.blueMid} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Panel>

                  {/* PPE class distribution donut */}
                  <Panel>
                    <SectionHead title="By PPE Class" sub="Distribution of all violations" />
                    <ResponsiveContainer width="100%" height={190}>
                      <PieChart>
                        <Pie
                          data={d.classDistribution} cx="50%" cy="50%"
                          innerRadius={50} outerRadius={78} paddingAngle={3}
                          dataKey="value"
                          label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {d.classDistribution.map((e, i) => <Cell key={i} fill={e.color} stroke="white" strokeWidth={2} />)}
                        </Pie>
                        <Tooltip content={<ChartTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 4 }}>
                      {d.classDistribution.map(z => (
                        <div key={z.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: z.color, flexShrink: 0, display: "inline-block" }} />
                            <span style={{ color: C.dark, fontWeight: 600 }}>{z.name}</span>
                          </span>
                          <span style={{ fontWeight: 800, fontSize: 14, color: z.color }}>{z.value}</span>
                        </div>
                      ))}
                      {d.classDistribution.length === 0 && (
                        <div style={{ textAlign: "center", color: C.gray, padding: "16px 0", fontSize: 12 }}>No data</div>
                      )}
                    </div>
                  </Panel>
                </div>

                {/* PPE class trend */}
                {d.classTimeSeries.length > 0 && (
                  <Panel>
                    <SectionHead title="PPE Class Trend" sub="Violations per PPE class over time" />
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={d.classTimeSeries}>
                        <defs>
                          {d.classKeys.map((key, i) => (
                            <linearGradient key={key} id={`hcg${i}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor={PALETTE[i % PALETTE.length]} stopOpacity={0.18} />
                              <stop offset="95%" stopColor={PALETTE[i % PALETTE.length]} stopOpacity={0} />
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0eeea" vertical={false} />
                        <XAxis dataKey="time" {...axisProps} />
                        <YAxis {...axisProps} allowDecimals={false} />
                        <Tooltip content={<ChartTooltip />} />
                        {d.classKeys.map((key, i) => (
                          <Area key={key} type="monotone" dataKey={key} name={ppeMeta(key).label}
                            stroke={PALETTE[i % PALETTE.length]} strokeWidth={2}
                            fill={`url(#hcg${i})`} dot={false} />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </Panel>
                )}

                {/* Person count trend */}
                {d.personCountTimeSeries.length > 0 && (
                  <Panel>
                    <SectionHead title="Person Count Trend" sub="Peak persons detected over time" />
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={d.personCountTimeSeries}>
                        <defs>
                          <linearGradient id="personCountGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={C.purple} stopOpacity={0.25} />
                            <stop offset="95%" stopColor={C.purple} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0eeea" vertical={false} />
                        <XAxis dataKey="time" {...axisProps} />
                        <YAxis {...axisProps} allowDecimals={false} />
                        <Tooltip content={<ChartTooltip />} />
                        <Area type="monotone" dataKey="count" name="Persons" stroke={C.purple} strokeWidth={2} fill="url(#personCountGrad)" dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Panel>
                )}
              </div>
            )}

            {/* ── CAMERA ANALYTICS ── */}
            {activeTab === "cameras" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                <Panel>
                  <SectionHead title="Violations by Camera" sub="Total PPE violations per surveillance point" />
                  <ResponsiveContainer width="100%" height={Math.max(d.aggregatedByCam.length * 52, 240)}>
                    <BarChart data={d.aggregatedByCam} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0eeea" horizontal={false} />
                      <XAxis type="number" {...axisProps} allowDecimals={false} />
                      <YAxis type="category" dataKey="cam" tick={{ fontSize: 12, fontWeight: 600, fill: C.dark }} width={140} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="count" name="Total Violations" radius={[0, 8, 8, 0]}>
                        {d.aggregatedByCam.map((e, i) => (
                          <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>

                <Panel>
                  <SectionHead title="Camera Rankings" sub="By total violation count and person count" />
                  {d.aggregatedByCam.length === 0 && (
                    <div style={{ textAlign: "center", color: C.gray, padding: "40px 0", fontSize: 13 }}>No data for selected date</div>
                  )}
                  {d.aggregatedByCam.map((cam, i) => {
                    const max = Math.max(...d.aggregatedByCam.map(c => c.count), 1);
                    const pct = (cam.count / max) * 100;
                    const barColor = PALETTE[i % PALETTE.length];
                    return (
                      <div key={cam.cam} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: i < d.aggregatedByCam.length - 1 ? `1px solid ${C.border}` : "none" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ width: 24, height: 24, borderRadius: "50%", background: `${barColor}18`, color: barColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                              #{i + 1}
                            </span>
                            <span style={{ fontWeight: 700, fontSize: 13, color: C.dark }}>{cam.cam}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            {Object.keys(cam.classCounts).slice(0, 3).map(k => <ClassPill key={k} cls={k} />)}
                            {cam.personCountEvents > 0 && (
                              <PersonCountPill count={cam.personCountTotal} />
                            )}
                            <span style={{ fontWeight: 800, fontSize: 16, color: C.dark }}>{cam.count}</span>
                          </div>
                        </div>
                        <div style={{ height: 6, background: C.grayLight, borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: barColor, borderRadius: 3, transition: "width 0.6s ease" }} />
                        </div>
                      </div>
                    );
                  })}
                </Panel>
              </div>
            )}

            {/* ── VIOLATIONS FEED ── */}
            {activeTab === "feed" && (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                  <SectionHead title="All Events" sub={`${filteredFeed.length} events · ${selectedDate.format("MMM D, YYYY")}`} />
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {["All", "person_count", ...d.classKeys].map(f => {
                      const active = feedFilter === f;
                      const m = f === "All"
                        ? { color: C.dark, bg: C.grayLight, label: "All" }
                        : f === "person_count"
                          ? { color: C.purple, bg: C.purpleLight, label: "Person Count" }
                          : ppeMeta(f);
                      return (
                        <button key={f} onClick={() => setFeedFilter(f)} style={{
                          padding: "5px 14px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer", border: "none",
                          background: active ? (m.color || C.dark) : (m.bg || C.grayLight),
                          color: active ? "#fff" : (m.color || C.dark),
                          transition: "all 0.15s", fontFamily: "inherit",
                        }}>{m.label}</button>
                      );
                    })}
                  </div>
                </div>
                <Panel style={{ padding: 0, overflow: "hidden" }}>
                  {filteredFeed.length === 0 ? (
                    <div style={{ textAlign: "center", color: C.gray, padding: "48px 0", fontSize: 13 }}>No events for selected filter</div>
                  ) : (
                    filteredFeed.map((e, i) => <FeedRow key={e._id || i} event={e} />)
                  )}
                </Panel>
              </div>
            )}

            {/* ── EVENT LOG TABLE ── */}
            {activeTab === "events" && (
              <div>
                <SectionHead title="Event Log" sub="Aggregated summary per camera — PPE violations and person counts" />
                <Panel style={{ padding: 0, overflow: "hidden" }}>
                  <Table
                    dataSource={d.aggregatedByCam}
                    columns={eventColumns}
                    rowKey="cam"
                    pagination={{ pageSize: 10, size: "small" }}
                    size="middle"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  />
                </Panel>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}